import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GiftBagImg from '../../../assets/bag.webp';
import GiftContainerImg from '../../../assets/gift-pack.webp';
import GiftGuideImg from '../../../assets/wellness-guide-cover.webp';
import GiftTrackerImg from '../../../assets/habit-tracker.webp';
import { Icons } from '../../elements/icon/resolver';
import { usePriceFormatter } from '../../../utils/orderCompleteUpsell';

const GIFT_IMAGES = {
  Gift_Bag: GiftBagImg,
  Gift_Container: GiftContainerImg,
  Gift_Guide: GiftGuideImg,
  Gift_Tracker: GiftTrackerImg,
};

const CircleCheckIcon = Icons.checkoutCircleCheck;
const ChevronDownIcon = Icons.checkoutChevronDown;

const OrderPaidSummary = ({ orderData, postPurchaseUpsell }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const recap = postPurchaseUpsell?.recap || {};
  const gifts = Array.isArray(recap.gifts) ? recap.gifts : [];
  const priceFmt = usePriceFormatter(i18n.language);
  const hasLockedGifts = gifts.some((gift) => gift.status === 'locked');
  const orderPrice = Number(orderData?.discounted_price || orderData?.totalPrice || 0);

  return (
    <div className="t-flex t-flex-col t-gap-2">
      <p className="!t-m-0 t-text-xs t-font-semibold t-text-gray-500 lg:t-text-[13px]">
        {t('OrderComplete_OrderPaid', { orderId: orderData?.id })}
      </p>
      <div className="t-flex t-items-center t-gap-2">
        <CircleCheckIcon className="t-h-[22px] t-w-[22px] t-flex-shrink-0 t-text-teal-default" />
        <div className="t-min-w-0">
          <p className="!t-m-0 t-text-[13px] t-font-bold t-leading-tight t-text-navy lg:t-text-[15px]">
            {t('OrderPaid_Title')}
          </p>
          <button
            type="button"
            onClick={() => setIsOpen((current) => !current)}
            aria-expanded={isOpen}
            className="t-flex t-items-center t-gap-1 t-border-0 t-bg-transparent t-p-0 t-text-xs t-font-semibold t-leading-tight t-text-teal-default lg:t-text-[13px]"
          >
            <span>{t('OrderPaid_ViewLink')}</span>
            <ChevronDownIcon className={`t-h-3 t-w-3 t-transition-transform ${isOpen ? 't-rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      {isOpen && (
        <div className="t-flex t-flex-col t-gap-2.5 t-pt-2">
          <div className="t-flex t-items-center t-justify-between t-gap-3 t-text-sm">
            <span className="t-text-xs t-font-semibold t-text-gray-700">{t(recap.lineItemKey || 'Recap_LineItem_1Month')}</span>
            <span className="t-text-xs t-font-bold t-text-navy">{priceFmt.format(orderPrice)}</span>
          </div>
          {recap.hasGifts && gifts.length > 0 && (
            <div className="t-flex t-flex-col t-gap-2">
              <p className="!t-m-0 t-flex t-items-center t-gap-1.5 t-text-[11.5px] t-font-bold t-text-navy">
                <span aria-hidden="true">🎁</span>
                <span>{t('Recap_GiftsHead_Owned')}</span>
              </p>
              <div className="t-grid t-grid-cols-4 t-gap-1.5">
                {gifts.map((gift) => (
                  <div
                    key={`${gift.key}-${gift.status}`}
                    className={`t-flex t-min-h-[92px] t-flex-col t-items-center t-gap-0.5 t-rounded-[10px] t-border t-px-1 t-py-1 ${
                      gift.status === 'locked'
                        ? 't-border-gray-200 t-bg-surface t-text-gray-400'
                        : 't-border-gray-200 t-bg-surface t-text-teal-default'
                    }`}
                  >
                    {GIFT_IMAGES[gift.key] && (
                      <span className="t-flex t-h-12 t-w-full t-items-center t-justify-center t-rounded-md t-bg-white t-p-0.5">
                        <img
                          src={GIFT_IMAGES[gift.key]}
                          alt={t(gift.key)}
                          className={`t-h-11 t-w-full t-object-contain ${gift.status === 'locked' ? 't-grayscale t-opacity-55' : ''}`}
                        />
                      </span>
                    )}
                    <span className="t-text-center t-text-[8.5px] t-font-semibold t-leading-tight t-text-navy">
                      {t(gift.key)}
                    </span>
                    <span className="t-text-center t-text-[8px] t-font-extrabold t-leading-tight">
                      {gift.status === 'locked' ? t('Gift_Locked') : t('Gift_Owned')}
                    </span>
                  </div>
                ))}
              </div>
              {hasLockedGifts && (
                <p className="!t-m-0 t-text-[11px] t-font-semibold t-leading-snug t-text-teal-default">
                  {t('Recap_Nudge_2More')}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderPaidSummary;
